import { FC, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@crisellevaldez/speculo-ui';

interface TableCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Table content — typically the speculo-ui <Table> or a wrapper around it */
  children: ReactNode;
  /** Footer content — typically <OffsetPagination /> */
  footer?: ReactNode;
  /** Extra classes for the outer card (e.g. height constraints like `h-[60vh]`) */
  className?: string;
  /** Extra classes for the inner body that holds the table */
  bodyClassName?: string;
  /** Extra classes for the footer wrapper */
  footerClassName?: string;
  /** Drops the outer border + shadow but keeps the rounded card surface — used in activity tabs / nested contexts */
  borderless?: boolean;
}

/**
 * Unified card wrapper for tables: bordered container with the table area on top
 * and an optional pagination footer separated by a divider.
 *
 * Pass any height constraint via `className` (e.g. `h-[60vh] sm:h-[calc(100vh-200px)]`).
 * The table body fills remaining space; the footer hugs its content.
 */
export const TableCard: FC<TableCardProps> = ({
  children,
  footer,
  className,
  bodyClassName,
  footerClassName,
  borderless = false,
  ...rest
}) => {
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg overflow-hidden bg-white dark:bg-transparent',
        borderless ? '' : 'border border-foreground/30 shadow-sm',
        className
      )}
      {...rest}
    >
      <div className={cn('flex-1 min-h-0', bodyClassName)}>{children}</div>
      {footer && (
        <div
          className={cn(
            borderless ? 'border-t border-foreground/15 bg-muted/20 dark:bg-muted/10' : 'border-t border-foreground/30 bg-muted/30 dark:bg-muted/20',
            footerClassName
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default TableCard;
